import { supabase } from './supabaseClient';
import { AAD_VAULT_ITEM_V1, decryptData, encryptData } from './cryptoService';

/**
 * Lit et déchiffre tous les items du coffre du titulaire (RLS auth.uid()).
 * Un item indéchiffrable est compté dans failedCount, jamais masqué.
 * @param {CryptoKey} masterKey - MK issue de useCrypto()
 * @returns {Promise<{ success: boolean, data: Array<{ id: string, category: string, data: object, updated_at: string }>, failedCount: number, error?: string }>}
 */
export const getAll = async (masterKey) => {
  if (!masterKey) return { success: false, error: 'Coffre verrouillé', data: [], failedCount: 0 };
  try {
    const { data: rows, error } = await supabase
      .from('vault_items')
      .select('id, category, encrypted_data, iv, updated_at');

    if (error) return { success: false, error: error.message, data: [], failedCount: 0 };

    const decrypted = [];
    let failedCount = 0;
    for (const row of rows || []) {
      try {
        const data = await decryptData(row.encrypted_data, row.iv, masterKey, AAD_VAULT_ITEM_V1(row.id));
        decrypted.push({ id: row.id, category: row.category, data, updated_at: row.updated_at });
      } catch {
        failedCount++;
      }
    }
    return { success: true, data: decrypted, failedCount };
  } catch (e) {
    return { success: false, error: e.message, data: [], failedCount: 0 };
  }
};

/**
 * Chiffre payload sous la MK (AAD DEC-017) et insère un nouvel item vault.
 * @param {object} params
 * @param {object} params.payload - { title, identifier, password, url, notes, label }
 * @param {string} params.category - une des 10 valeurs vault_items_category_check
 * @param {CryptoKey} params.masterKey - MK issue de useCrypto()
 * @param {string} params.userId
 * @returns {Promise<{ success: boolean, id?: string, created_at?: string, error?: string }>}
 */
export const createVaultItem = async ({ payload, category, masterKey, userId }) => {
  const itemId = crypto.randomUUID();
  const aad = `safeheir-vault-item-v1|${itemId}`;
  const { ciphertext, iv } = await encryptData(payload, masterKey, aad);

  const { data: inserted, error: insertError } = await supabase
    .from('vault_items')
    .insert({ id: itemId, user_id: userId, category, encrypted_data: ciphertext, iv })
    .select('id, created_at')
    .single();

  if (insertError) return { success: false, error: insertError.message };

  return { success: true, id: inserted.id, created_at: inserted.created_at };
};

export const getDistinctCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('vault_items')
      .select('category');

    if (error) return { success: false, error: error.message, data: [] };

    const distinct = [...new Set((data || []).map(r => r.category).filter(Boolean))];
    return { success: true, data: distinct };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};
